import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";

// Default sections
import { ComplexCommission, ComplexCommissionTier } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isLastValid, isValidMatrixRange } from "@/Common/helpers/Matrices/tiers";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// Tier components
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierExpandableHeader from "@/Layouts/components/Tiers/TierExpandableHeader";
import TierRows from "@/Layouts/components/Tiers/TierRows";

// MUI
import { Grid, Stack } from "@mui/material";

const inputsMap = new Map([
	[
		"Dollars",
		[
			{ type: "Dollar", decimalsEditable: true, decimals: 2 },
			{ type: "Percent", decimalsEditable: true, decimals: 2 },
		],
	],
	[
		"Contracts",
		[
			{ type: "Unit", decimalsEditable: false, delta: 1 },
			{ type: "Percent", decimalsEditable: true, decimals: 2 },
		],
	],
]);

const useCITTypes = (types = []) => types.map((type) => ({ _id: type, label: type }));

const getInputDefinitions = (relative = false) => {
	if (relative) {
		// Values relative to store
		return {
			daysInTransit: {
				min: -365,
				max: 365,
			},
			contractsInTransit: {
				Dollar: {
					min: -100000000,
					max: 100000000,
				},
				Percent: {
					min: -100,
					max: 100,
				},
				Unit: {
					min: -100000000,
					max: 100000000,
				},
			},
		};
	}

	return {
		daysInTransit: {
			min: 0,
			max: 365,
		},
		contractsInTransit: {
			Dollar: {
				min: 0,
				max: 100000000,
			},
			Percent: {
				min: 0,
				max: 100,
			},
			Unit: {
				min: 0,
				max: 100000000,
			},
		},
	};
};

const useInputDefinitions = (relative = false) => getInputDefinitions(relative);

const updateTiersLimits = (tiers = [], definitions = {}) => {
	const { ranges, headRanges, inputTierDefault = "Dollar" } = definitions;
	if (!tiers?.length) {
		return tiers;
	}

	const rows = tiers.length - 1;
	const columns = tiers[0]?.bonuses?.length - 1;

	const adjustTierValues = (tier = [], input = "Dollar") => ({
		...tier,
		from: {
			input,
			value: 0,
		},
		to: {
			input,
			value: 0,
		},
	});

	tiers.forEach((tier, key) => {
		tiers[key] = adjustTierValues(tier, inputTierDefault);
		tiers[key]["bonuses"] = tiers[key]["bonuses"].map((bonus) => adjustTierValues(bonus, "Unit"));
		tiers[key]["bonuses"][0]["from"]["value"] = headRanges.max;
		tiers[key]["bonuses"][columns]["to"]["value"] = headRanges.min;
	});

	tiers[0]["from"]["value"] = ranges.max;
	tiers[rows]["to"]["value"] = ranges.min;

	return tiers;
};

const getErrorsDefinition = (errors = new Map(), identifier = "") => {
	if (!identifier) {
		return undefined;
	}
	return errors?.get(identifier);
};

export default function ContractsInTransit(props) {
	const {
		model = {
			citType: "Dollars",
			relativeToStore: false,
			tiers: [],
		},
		citTypes = [],
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	// Specific use of concatenatedErrors to get all errors in one list
	const citOptionTypes = useCITTypes(citTypes);
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	// Errors
	const headerErrors = getErrorsDefinition(errors, "daysInTransit");
	const rowsErrors = getErrorsDefinition(errors, "contracts");
	const columnsErrors = getErrorsDefinition(errors, "bonuses");
	const lastBonusError = Boolean(getErrorsDefinition(errors, "lastBonus")?.length);
	const minimumRangeError = Boolean(getErrorsDefinition(errors, "minTiersRequired")?.length);

	// Control to send update actions / References
	const wasUpdated = useRef(false);

	const onModelUpdate = (updatedModel) => {
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(updatedModel);
		}
	};

	// States
	const [innerModel, setInnerModel] = useCallbackState([], onModelUpdate);
	const inputDefinitions = useInputDefinitions(innerModel?.relativeToStore);

	// Methods
	const handleAddColumn = () => {
		const tiers = [...innerModel.tiers];
		const minRange = inputDefinitions?.daysInTransit?.min;
		const lastIndex = tiers?.[0]?.["bonuses"]?.length ? tiers[0]["bonuses"].length - 1 : 0;
		const bonusInput = tiers?.[0]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";

		tiers.forEach((tier) => {
			tier.bonuses.push(ComplexCommission([0, "Unit"], [minRange, "Unit"], { commission: [0, bonusInput] }));
			if (tier?.bonuses?.[lastIndex]) {
				tier.bonuses[lastIndex]["to"]["value"] = 0;
			}
		});

		wasUpdated.current = false;
		setInnerModel({ ...innerModel, tiers });
	};

	const handleDeleteColumn = () => {
		const tiers = [...innerModel.tiers];
		const minRange = inputDefinitions?.daysInTransit?.min;

		if (tiers?.[0]?.["bonuses"]?.length > 1) {
			tiers.forEach((tier) => {
				tier.bonuses.splice(-1);
				const lastIndex = tier?.bonuses?.length ? tier.bonuses.length - 1 : 0;
				tier.bonuses[lastIndex]["to"]["value"] = minRange;
			});
		}
		update({ ...innerModel, tiers });
	};

	const handleAddTier = () => {
		const tiers = [...innerModel.tiers];
		const lastIndex = tiers?.length ? tiers.length - 1 : 1;
		const lastInput = tiers?.[lastIndex]?.["from"]?.["input"] || "Dollar";
		const lastCommissionInput = tiers?.[lastIndex]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const ranges = inputDefinitions?.contractsInTransit?.[lastInput];
		const previousBonusesLength = tiers?.[lastIndex]?.["bonuses"]?.length;

		const bonuses = [];
		const previousFrom = tiers?.[0]?.["bonuses"]?.[0]?.["from"]?.["value"] || 0;
		const previousTo = tiers?.[0]?.["bonuses"]?.[0]?.["to"]?.["value"] || 0;

		for (let i = 0; i < previousBonusesLength; i++) {
			bonuses[i] = ComplexCommission([previousFrom, "Unit"], [previousTo, "Unit"], { commission: [0, lastCommissionInput] });
		}

		tiers.push(ComplexCommissionTier([0, lastInput], [ranges?.min || 0, lastInput], { identifier: "bonuses", commissions: bonuses }));

		if (tiers?.length) {
			tiers[lastIndex].to.value = 0;
		}

		wasUpdated.current = false;
		setInnerModel({ ...innerModel, tiers });
	};

	const handleDeleteTier = () => {
		const tiers = [...innerModel.tiers];
		if (tiers.length >= 1) {
			tiers.splice(-1);
		}

		const lastIndex = tiers?.length ? tiers.length - 1 : 1;
		const lastInput = tiers[lastIndex].to.input || "Dollar";
		const ranges = inputDefinitions?.contractsInTransit?.[lastInput];
		tiers[lastIndex].to.value = ranges?.min || 0;

		update({ ...innerModel, tiers });
	};

	const handleUpdateValue = (row, column, location, area, value) => {
		const tiers = [...innerModel.tiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				tiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus, key) => {
						if (area in bonus && key === column) {
							bonus[area]["value"] = updatedValue;
							if (tier.bonuses?.[key + 1]) {
								tier.bonuses[key + 1]["from"]["value"] = (updatedValue - 0.01).toFixed(2);
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "bonus") {
					tiers[row][area]["value"] = updatedValue;
					if (tiers?.[row + 1] && area === "to") {
						tiers[row + 1]["from"]["value"] = updatedValue - 0.01;
					}
				} else {
					tiers[row]["bonuses"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}

		update({ ...innerModel, tiers });
	};

	const handleUpdateInput = (_row, _column, location, area, input) => {
		const tiers = [...innerModel.tiers];
		const ranges = inputDefinitions?.contractsInTransit;

		switch (location) {
			case "column":
				break;
			case "row":
				if (area !== "bonus") {
					tiers.forEach((tier) => {
						tier["from"]["input"] = input;
						tier["to"]["input"] = input;
						tier["from"]["value"] = 0;
						tier["to"]["value"] = 0;
					});

					tiers[0]["from"]["value"] = ranges?.[input]?.max;
					tiers[tiers.length - 1]["to"]["value"] = ranges?.[input]?.min;
				} else {
					tiers.forEach((tier) => {
						tier.bonuses?.forEach((bonus) => {
							if (area in bonus) {
								bonus[area]["input"] = input;
								bonus[area]["value"] = 0;
							}
						});
					});
				}
				break;
			default:
				break;
		}

		update({ ...innerModel, tiers });
	};

	const handleChangeCITType = (value) => {
		let inputTierDefault = "Dollar";
		switch (value?.toUpperCase()) {
			case "DOLLARS":
				break;
			case "CONTRACTS":
				inputTierDefault = "Unit";
				break;
			default:
				break;
		}

		const headRanges = inputDefinitions?.daysInTransit;
		const ranges = inputDefinitions?.contractsInTransit?.[inputTierDefault] || { min: 0, max: 10000000 };

		const updatedTiers = innerModel?.tiers || [];
		const tiersToUpdate = updateTiersLimits([...updatedTiers], {
			ranges,
			headRanges,
			inputTierDefault,
		});

		update({ ...innerModel, citType: value, tiers: tiersToUpdate });
	};

	const handleChangeRelative = (value) => {
		const tiers = [...innerModel.tiers];
		const lastIndex = tiers?.length ? tiers.length - 1 : 1;
		const lastInput = tiers[lastIndex].to.input || "Dollar";

		const rangeDefinitions = getInputDefinitions(value);
		const headRanges = rangeDefinitions?.daysInTransit;
		const ranges = rangeDefinitions?.contractsInTransit?.[lastInput] || { min: 0, max: 10000000 };

		tiers.forEach((tier) => {
			tier.bonuses[0]["from"]["value"] = headRanges.max;
			tier.bonuses[tier.bonuses.length - 1]["to"]["value"] = headRanges.min;
		});

		tiers[0]["from"]["value"] = ranges?.max;
		tiers[lastIndex]["to"]["value"] = ranges?.min;

		update({ ...innerModel, relativeToStore: value, tiers });
	};

	const update = (updatedModel) => {
		wasUpdated.current = true;
		setInnerModel(updatedModel);
	};

	useMemo(() => {
		if (innerModel?.tiers?.length) {
			validate([
				{
					definition: {
						useKey: "contracts",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">", // If from > to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: ">", // If from > to is good,
									deltas: { Dollar: 0, Percent: 0, Unit: 0 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerModel?.tiers || [],
				},
				{
					definition: {
						useKey: "daysInTransit",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">", // If from to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Dollar: 0, Percent: 0, Unit: -0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid days in transit tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerModel?.tiers[0]?.bonuses || [],
				},
				{
					definition: {
						useKey: "bonuses",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<", // If current < next is good,
									constraints: [
										{
											on: "bonus",
											constraint: "oneAdjacentZero",
											apply: Boolean(innerModel?.relativeToStore),
										},
									],
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<", // If upper < lower is good,
									deltas: { Percent: 0, Dollar: 0 },
								},
							],
						},
						errorsMap: ["bonus"],
						errorsMapOn: "bonuses",
						message: "Incorrect bonuses definition",
						validationType: "MATRIX",
					},
					section: innerModel?.tiers || [],
				},
				{
					definition: {
						useKey: "lastBonus",
						validationType: "CUSTOM",
						errorsMap: ["bonus"],
						message: "Last commission must be configured",
						operators: {
							customFn: (params = null) => {
								const { tiers = [] } = params || {};
								const lastValid = isLastValid(tiers, { column: "bonuses", reference: "bonus" });
								return [{ bonus: !lastValid }];
							},
							params: {
								tiers: innerModel?.tiers || [],
							},
						},
					},
					section: [{ bonus: false }],
				},
				{
					definition: {
						useKey: "minTiersRequired",
						validationType: "CUSTOM",
						errorsMap: ["tiers"],
						message: "Please add at least a two tiers configuration",
						operators: {
							customFn: (params = null) => {
								const { tiers } = params || {};
								const isValidRange = isValidMatrixRange(tiers, { column: "bonuses" });
								return [{ tiers: !isValidRange }];
							},
							params: {
								tiers: innerModel?.tiers,
							},
						},
					},
					section: [{ tiers: false }],
				},
			]);
		}
	}, [innerModel, validate]);

	useEffect(() => {
		setInnerModel(clone(model));

		return () => {
			setInnerModel(null);
			wasUpdated.current = false;
		};
	}, [model, setInnerModel]);

	return (
		<Grid container item xs={12}>
			<Grid item xs={12}>
				<TierExpandableHeader
					headers={[
						{
							component: (
								<Stack mb={2} ml={"-34px"} minWidth={"145px"}>
									<SmartSelector
										options={citOptionTypes}
										selected={innerModel?.citType || "Dollar"}
										inputProps={{
											placeholder: "Select CIT Type...",
										}}
										listBoxProps={{
											sx: { maxHeight: "160px" },
										}}
										disabled={readOnly}
										onChangeAction={(selected) => handleChangeCITType(selected._id)}
									/>
								</Stack>
							),
						},
						{
							component: (
								<Stack direction={"row"}>
									<CustomizedSwitches
										textLeft
										textChecked="Relative to Store"
										textUnChecked="Relative to Self"
										checked={Boolean(innerModel?.relativeToStore)}
										onChange={handleChangeRelative}
										disabled={readOnly}
										tooltip={{
											title: "Measures the funding efficiency based on number of deals touched in commission cycle, or relative to the store average efficiency per finance manager.",
											placement: "bottom",
											key: "cit-bonus-tooltip",
										}}
									/>
								</Stack>
							),
							style: { position: "absolute", right: "30px" },
						},
					]}
					containerProps={{
						direction: "row",
						justifyContent: "flex-start",
					}}
				/>
			</Grid>

			<Grid item xs={12} mt={2} ml={"32.5px"}>
				<TierColumns
					titleLabel={"Avg. Days In Transit"}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "end",
						sx: { maxWidth: "203px", width: "203px" },
					}}
					titleProps={{
						className: "tiered-title tiered-title-adjustment",
						sx: null,
					}}
					disabledDelete={innerModel?.tiers?.[0]?.bonuses?.length <= 1 || readOnly}
					disabledAdd={innerModel?.tiers?.[0]?.bonuses?.length >= 7 || readOnly}
					onClickDelete={() => handleDeleteColumn()}
					onClickAdd={() => handleAddColumn()}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerModel?.tiers?.[0]?.bonuses?.map((tier, key) => {
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerModel?.tiers?.[0]?.bonuses.length === key + 1);
							const itemKey = `tier_column_cit_${key}`;
							return (
								<TierColumnsItem key={itemKey} index={key} title={"Bonus"}>
									<MatrixColumn
										index={key}
										item={tier}
										ranges={inputDefinitions?.daysInTransit}
										errors={headerErrors}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										readOnly={readOnly}
										onActionUpdate={handleUpdateValue}
										onChangeType={handleUpdateInput}
									/>
								</TierColumnsItem>
							);
						})}
					</Stack>
				</TierColumns>
			</Grid>

			<Grid item xs={12} mt={1} minHeight={"100px"}>
				<TierRows
					showTitle
					title={`${innerModel?.citType} in Transit`}
					sxTitlePropsContainer={{
						top: "84px",
						left: innerModel?.citType === "Dollars" ? "-19px" : "-30px",
					}}
					centerTitleAfterRow={1}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerModel?.tiers?.length <= 1 || readOnly}
					addButtonDisabled={innerModel?.tiers?.length >= 50 || readOnly}
					spaces={{
						mainSection: 234,
						columns: 109,
						totalColumns: innerModel?.tiers?.[0]?.bonuses?.length || 1,
						totalRows: innerModel?.tiers?.length || 1,
						separation: 5.5,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={() => handleDeleteTier()}
					onClickAdd={() => handleAddTier()}
				>
					<Stack direction="column" rowGap={2}>
						{innerModel?.tiers?.map((tier, key) => {
							const itemKey = `tier_row_cit_${key}`;
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerModel?.tiers?.length === key + 1);
							const bonusesLength = innerModel?.tiers?.[0]?.bonuses?.length || 0;
							const reduction = bonusesLength * 1.55 + 2.5 + bonusesLength;

							const getColumnsErrors = (nativeErrors = [], complainErrors = false) => {
								if (nativeErrors?.length) {
									return nativeErrors;
								}
								return [[{ bonus: complainErrors }]];
							};

							return (
								<Grid key={itemKey} container item>
									<MatrixRow
										index={key}
										item={tier}
										inputDataDefinitions={inputsMap?.get(innerModel?.citType)}
										inputRanges={inputDefinitions?.contractsInTransit}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										spaces={{
											mainSection: 226,
											columns: 101,
											totalColumns: innerModel?.tiers?.[0]?.bonuses?.length || 1,
											totalRows: innerModel?.tiers?.tiers?.length || 1,
											separation: 5.5,
											reduction,
										}}
										rowsErrors={rowsErrors}
										columnsErrors={getColumnsErrors(columnsErrors, minimumRangeError)}
										lastBonusError={lastBonusError}
										readOnly={readOnly}
										spacing={"9px"}
										sxMainContent={{ minWidth: "226.5px" }}
										sxCells={{ minWidth: "100.42px" }}
										onActionUpdate={handleUpdateValue}
										onChangeType={handleUpdateInput}
									/>
								</Grid>
							);
						})}
					</Stack>

					{Boolean(localErrorsMessages?.length) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => (
								<TierAlertError key={`error_cit_${message}_${key}`} text={message} />
							))}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
ContractsInTransit.propTypes = {
	model: PropTypes.shape({
		citType: PropTypes.string,
		relativeToStore: PropTypes.bool,
		tiers: PropTypes.array,
	}),
	citTypes: PropTypes.array,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
